import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  CheckIcon,
  EnvelopeIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import { parseArTaskAction } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { useToast } from 'src/context/ToastContext';
import { orpc } from '@/api/rpc/client';
import { OPEN_TASK_STATUSES } from '@/api/rpc/hooks/accounts-receivable/useArTasks';
import { useResolveArPaymentReminderCustomers } from '@/api/rpc/hooks/accounts-receivable/useArPaymentReminderCustomers';
import { invalidateTaskQueries } from '@/api/rpc/hooks/tasks/invalidateTaskQueries';
import { SendStatementDialog } from '../statements/BulkSendStatementDialog';
import { SendInvoiceDialog } from '@/components/accounts-receivable/invoice-detail/SendInvoiceDialog';
import type { ArReportFilters } from '@/components/accounts-receivable/ar-report-filters';
import type {
  ArAgingBasis,
  ArBulkStatementSendAction,
  ArInvoiceSendAction,
  ArStatementSendTaskCustomer,
} from '@erp/shared-types';
import type { TaskStatus } from '@erp/rpc';

interface ArTaskActionButtonProps {
  /**
   * Minimal task shape the button needs — satisfied by both the RPC
   * `TaskResponse` (AR summary card) and the inbox's mapped `Task` (via the
   * generic `TaskActionButton` dispatcher).
   */
  task: {
    id: string;
    status: TaskStatus;
    metadata?: Record<string, unknown> | null;
  };
  /**
   * Drop the "Open thread" deep link for the conversation kinds. Set by the
   * collection-thread reminder banner, which already renders *on* that thread
   * — the link would just point at the page the user is looking at. The
   * complete button still renders.
   */
  hideThreadLink?: boolean;
}

/**
 * Inline action for AR workflow tasks, rendered on the summary dashboard card
 * and in the inbox task detail pane. Dispatches on the task's `arAction`
 * metadata payload:
 *
 * - `ar_bulk_statement_send` → "Send statements" opens the bulk statement
 *   send dialog pre-populated with the queued customers (mirroring the scan's
 *   aging basis + day range).
 * - `ar_invoice_send` → "Send invoices" opens the bulk invoice send dialog
 *   pre-populated with the queued created-but-never-sent invoices.
 * - `ar_conversation_reply_review` / `ar_conversation_no_response` →
 *   "Open thread" deep-links to the collection thread, where the suggested
 *   reply draft (or the unanswered send) lives.
 *
 * Every kind gets a one-click complete button. Tasks without a recognized
 * payload render nothing — the row still links to the inbox task pane.
 */
export function ArTaskActionButton({
  task,
  hideThreadLink,
}: ArTaskActionButtonProps) {
  const action = parseArTaskAction(task.metadata);
  if (!action) return null;
  // The 30s task poll can briefly show a task someone else just completed or
  // cancelled — don't offer send/complete actions on a task no longer open.
  if (!OPEN_TASK_STATUSES.includes(task.status)) return null;

  return (
    <>
      {action.kind === 'ar_bulk_statement_send' ? (
        <StatementSendAction action={action} taskId={task.id} />
      ) : action.kind === 'ar_invoice_send' ? (
        <InvoiceSendAction action={action} />
      ) : hideThreadLink ? null : (
        <OpenThreadAction threadId={action.threadId} />
      )}
      <CompleteTaskButton taskId={task.id} />
    </>
  );
}

/**
 * Complete-task mutation shared by the manual complete button and the
 * auto-complete-on-send flow, so the success/error toasts and cache
 * invalidation stay identical however the task gets completed.
 */
function useCompleteArTask() {
  const gt = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    orpc.tasks.complete.mutationOptions({
      onSuccess: () => {
        invalidateTaskQueries(queryClient);
        showToast({
          color: 'success',
          message: gt('Task completed', {
            context: 'toast after completing an AR task from the dashboard',
          }),
        });
      },
      onError: () => {
        showToast({
          color: 'error',
          message: gt('Could not complete the task', {
            context: 'toast when completing an AR task fails',
          }),
        });
      },
    }),
  );
}

function CompleteTaskButton({ taskId }: { taskId: string }) {
  const gt = useGT();
  const completeTask = useCompleteArTask();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 w-7 p-0"
      title={gt('Mark task complete', {
        context: 'tooltip for the complete button on an AR task row',
      })}
      disabled={completeTask.isPending}
      onClick={() => completeTask.mutate({ id: taskId })}
    >
      <CheckIcon className="size-3.5" />
      <span className="sr-only">
        <T context="screen reader label for the complete button on an AR task row">
          Mark task complete
        </T>
      </span>
    </Button>
  );
}

function StatementSendAction({
  action,
  taskId,
}: {
  action: ArBulkStatementSendAction;
  taskId: string;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [sendOpen, setSendOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  // The customer set the dialog will open with, and an optional notice about how
  // it differs from the task's snapshot. Populated by the click handler below.
  const [resolved, setResolved] = useState<{
    customers: ArStatementSendTaskCustomer[];
    notice?: string;
  } | null>(null);
  // Set once the user actually sends at least one statement in this dialog
  // session. We defer completing the task until the dialog closes: completing
  // it immediately would flip the task out of an open status and unmount this
  // whole action (and the dialog) mid-flow, cutting off the send summary.
  const sentSomethingRef = useRef(false);
  const completeTask = useCompleteArTask();
  const resolveCustomers = useResolveArPaymentReminderCustomers();

  /**
   * Resolve who still needs a reminder, then open the dialog with that set —
   * rather than trusting the list the scan snapshotted into the task. Statements
   * sent by hand since the scan (from the aging report, a customer page, an
   * earlier pass at this task) update the customer's last-sent date, so the
   * server's cooldown filter drops them here and the user is never asked to send
   * the same statement twice.
   *
   * Resolved on click, not on mount: the AR summary card renders one of these
   * per task row and shouldn't fire a report-sized aging query for tasks nobody
   * opens. The reminder terms are read server-side off the task itself, so only
   * the task id goes over the wire.
   */
  async function handleOpenSend() {
    setResolving(true);
    try {
      const customers = await resolveCustomers(taskId);
      if (customers.length === 0) {
        // Everything on the task has since been sent. Say so instead of opening
        // an empty dialog. The task stays open so completing it remains the
        // user's call (the next scan cancels it anyway).
        showToast({
          color: 'success',
          message: gt(
            'Every customer on this task has already received a statement.',
            {
              context:
                'toast when a payment-reminder task has no customers left to send to',
            },
          ),
        });
        return;
      }
      const removedCount = Math.max(
        0,
        action.customers.length - customers.length,
      );
      setResolved({
        customers,
        notice:
          removedCount > 0
            ? gt(
                '{count} of the customers on this task already received a statement and were removed.',
                {
                  context:
                    'notice in the statement send dialog when the live customer list is smaller than the task snapshot',
                  count: removedCount,
                },
              )
            : undefined,
      });
      setSendOpen(true);
    } catch {
      // Fall back to the task's snapshot — a stale list beats a dead button, and
      // the dialog still refuses zero-invoice sends per customer.
      setResolved({
        customers: action.customers,
        notice: gt(
          "Couldn't refresh this list, so it may include customers who already received a statement.",
          {
            context:
              'warning in the statement send dialog when the live customer list could not be resolved',
          },
        ),
      });
      setSendOpen(true);
    } finally {
      setResolving(false);
    }
  }

  const reportSettings: ArReportFilters = {
    // Statement *content* settings, still read from the payload: which invoices
    // land on the PDF, independent of who qualified for a reminder.
    basis: action.agingBasis as ArAgingBasis,
    // Content uses its own (independent) age floor — not the trigger
    // threshold that decided customer eligibility. Defaults to 0, i.e. ALL
    // outstanding invoices.
    minDays: action.contentMinInvoiceAgeDays,
    maxDays: undefined,
    currentOnly: false,
  };

  function handleOpenChange(nextOpen: boolean) {
    setSendOpen(nextOpen);
    if (!nextOpen && sentSomethingRef.current) {
      sentSomethingRef.current = false;
      completeTask.mutate({ id: taskId });
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1 px-2 text-xs"
        disabled={resolving}
        onClick={() => void handleOpenSend()}
      >
        {resolving ? (
          <SpinnerIcon className="size-3.5 animate-spin" />
        ) : (
          <EnvelopeIcon className="size-3.5" />
        )}
        <T context="button on an AR task row that opens the bulk statement send dialog">
          Send statements
        </T>
      </Button>
      {sendOpen && resolved && (
        <SendStatementDialog
          open={sendOpen}
          onOpenChange={handleOpenChange}
          customers={resolved.customers.map((c) => ({
            customerId: c.customerId,
            customerName: c.customerName,
            openBalance: c.openBalance,
            lastStatementSentAt: c.lastStatementSentAt,
          }))}
          reportSettings={reportSettings}
          // Recipient roles, also from the payload: the workflow step may
          // override how this run's statements are addressed. `undefined` when
          // the step didn't configure one, which leaves the dialog on the org's
          // AR setting.
          statementRecipientRolesOverride={action.statementRecipientRoles}
          onStatementSent={() => {
            sentSomethingRef.current = true;
          }}
          // Before "Send All", refresh every queued customer's invoices from
          // Famous so statements never carry stale amounts for customers the
          // user didn't individually review (the dialog resolves each open
          // invoice set server-side from these customers).
          refreshBeforeSendAll
          notice={resolved.notice}
        />
      )}
    </>
  );
}

/**
 * Deep link to the collection thread for agent-conversation tasks (reply
 * review + no-response follow-up): the suggested draft and the conversation
 * timeline both live on the thread.
 */
function OpenThreadAction({ threadId }: { threadId: string }) {
  const navigate = useNavigate();

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 gap-1 px-2 text-xs"
      onClick={() =>
        void navigate({
          to: '/accounts-receivable/collections',
          search: { tab: 'threads', view: 'list', threadId },
        })
      }
    >
      <ArrowSquareOutIcon className="size-3.5" />
      <T context="button on an AR task row that opens the linked collection thread">
        Open thread
      </T>
    </Button>
  );
}

function InvoiceSendAction({ action }: { action: ArInvoiceSendAction }) {
  const [sendOpen, setSendOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1 px-2 text-xs"
        onClick={() => setSendOpen(true)}
      >
        <EnvelopeIcon className="size-3.5" />
        <T context="button on an AR task row that opens the bulk invoice send dialog">
          Send invoices
        </T>
      </Button>
      {sendOpen && (
        <SendInvoiceDialog
          open={sendOpen}
          onOpenChange={setSendOpen}
          invoices={action.invoices.map((inv) => ({
            invoiceId: inv.invoiceId,
            customerId: inv.customerId,
            customerName: inv.customerName,
            invoiceNumber: inv.invoiceNumber,
          }))}
        />
      )}
    </>
  );
}
